import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Slider } from './ui/slider';
import {
  Play, Pause, SkipForward, SkipBack, Volume2, VolumeX, Shuffle, Upload,
  Trash2, Music, ChevronUp, ChevronDown, ArrowLeft, ToggleLeft, ToggleRight,
} from 'lucide-react';
import { toast } from 'sonner';
import AppBranding from './AppBranding';

const API_V2 = process.env.REACT_APP_BACKEND_URL + '/api/v2';

const MusicPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const audioRef = useRef(null);

  const [tracks, setTracks] = useState([]);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [vol, setVol] = useState(50);
  const [muted, setMuted] = useState(false);
  const [shuffleOn, setShuffle] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [audioError, setAudioError] = useState(null);

  const isAdmin = user?.is_admin || user?.role === 'admin' || ['GOD', 'ADMIN'].includes(user?.access_level);

  // Fetch tracks
  const fetchTracks = useCallback(async () => {
    try {
      const res = await axios.get(`${API_V2}/music/tracks`, { withCredentials: true });
      setTracks(res.data.tracks || []);
    } catch {}
  }, []);

  useEffect(() => { fetchTracks(); }, [fetchTracks]);

  const fmt = (s) => {
    if (!s || isNaN(s)) return '0:00';
    return `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}`;
  };

  // Track blob URLs for cleanup
  const blobUrlRef = useRef(null);

  // ---- PLAY FUNCTION (blob-based for Safari/proxy compatibility) ----
  const playTrack = async (track) => {
    const audio = audioRef.current;
    if (!audio || !track) return;
    setAudioError(null);

    // Same track: toggle play/pause
    if (currentTrack?.track_id === track.track_id) {
      if (audio.paused) {
        audio.play().catch(e => {
          console.error('Resume error:', e);
          setAudioError(e.message);
        });
      } else {
        audio.pause();
      }
      return;
    }

    // Different track: fetch as blob and play
    setCurrentTrack(track);
    setProgress(0);
    setDuration(0);

    // Clean up previous blob URL
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }

    const url = `${API_V2}/music/tracks/${track.track_id}/stream`;

    try {
      // Fetch the entire audio file as a blob
      const response = await fetch(url);
      if (!response.ok) {
        setAudioError(`Server error: ${response.status} - File may need to be re-uploaded`);
        return;
      }

      const blob = await response.blob();
      // Create blob URL with explicit audio MIME type
      const audioBlob = new Blob([blob], { type: 'audio/mpeg' });
      const blobUrl = URL.createObjectURL(audioBlob);
      blobUrlRef.current = blobUrl;

      // Set audio source to blob URL and play
      audio.src = blobUrl;
      audio.play().catch(e => {
        console.error('Play error:', e);
        setAudioError(e.message);
      });
    } catch (err) {
      console.error('Fetch audio error:', err);
      setAudioError(`Failed to load audio: ${err.message}`);
    }
  };

  const playNext = () => {
    const enabled = tracks.filter(t => t.enabled !== false);
    if (enabled.length === 0) return;
    if (shuffleOn) {
      const others = enabled.filter(t => t.track_id !== currentTrack?.track_id);
      const pick = others.length > 0 ? others[Math.floor(Math.random() * others.length)] : enabled[0];
      playTrack(pick);
    } else {
      const idx = enabled.findIndex(t => t.track_id === currentTrack?.track_id);
      playTrack(enabled[(idx + 1) % enabled.length]);
    }
  };

  const playPrev = () => {
    const audio = audioRef.current;
    if (audio && audio.currentTime > 3) { audio.currentTime = 0; return; }
    const enabled = tracks.filter(t => t.enabled !== false);
    if (enabled.length === 0) return;
    const idx = enabled.findIndex(t => t.track_id === currentTrack?.track_id);
    playTrack(enabled[(idx - 1 + enabled.length) % enabled.length]);
  };

  // Chunked upload
  const CHUNK_SIZE = 512 * 1024;
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadProgress(0);
    try {
      const initRes = await axios.post(`${API_V2}/music/upload/init`, {
        filename: file.name, total_size: file.size,
        title: uploadTitle || file.name.replace(/\.[^.]+$/, ''),
        artist: 'BitBot AI',
      }, { withCredentials: true });
      const { upload_id } = initRes.data;
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const slice = file.slice(start, Math.min(start + CHUNK_SIZE, file.size));
        const arrayBuffer = await slice.arrayBuffer();
        const base64 = btoa(new Uint8Array(arrayBuffer).reduce((d, b) => d + String.fromCharCode(b), ''));
        await axios.post(`${API_V2}/music/upload/chunk`, { upload_id, chunk_index: i, data: base64 }, { withCredentials: true });
        setUploadProgress(Math.round(((i + 1) / totalChunks) * 100));
      }
      await axios.post(`${API_V2}/music/upload/finalize`, { upload_id }, { withCredentials: true });
      toast.success('Track uploaded');
      setUploadTitle('');
      fetchTracks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      e.target.value = '';
    }
  };

  const handleDelete = async (trackId) => {
    try {
      await axios.delete(`${API_V2}/music/tracks/${trackId}`, { withCredentials: true });
      toast.success('Track deleted');
      if (currentTrack?.track_id === trackId) {
        audioRef.current?.pause();
        setCurrentTrack(null);
        setIsPlaying(false);
      }
      fetchTracks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const handleToggle = async (trackId) => {
    try {
      const res = await axios.put(`${API_V2}/music/tracks/${trackId}/toggle`, {}, { withCredentials: true });
      toast.success(res.data.message);
      fetchTracks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Toggle failed');
    }
  };

  return (
    <div className="min-h-screen bg-black text-[#00FF41] font-mono">
      {/* The actual audio element - plain HTML, no React abstraction */}
      <audio
        ref={audioRef}
        preload="auto"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onLoadedMetadata={() => setDuration(audioRef.current?.duration || 0)}
        onTimeUpdate={() => setProgress(audioRef.current?.currentTime || 0)}
        onEnded={playNext}
        onError={() => {
          const err = audioRef.current?.error;
          if (err) {
            console.error('Audio error:', err.code, err.message);
            setAudioError(err.message);
          }
          setIsPlaying(false);
        }}
      />

      <AppBranding />
      <div className="max-w-3xl mx-auto px-4 py-6">
        <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-[#008F11] hover:text-[#00FF41] mb-4 px-0" data-testid="music-back-btn">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to Dashboard
        </Button>

        <h1 className="text-2xl font-bold tracking-widest mb-6 flex items-center gap-2" data-testid="music-page-title">
          <Music className="h-6 w-6" /> BITBOT JUKEBOX
        </h1>

        {/* Main Player Card */}
        <Card className="bg-[#0a0a0a] border border-[#003B00] mb-6" data-testid="music-player-card">
          <CardContent className="p-6">
            <div className="text-center mb-4">
              <div className="w-24 h-24 mx-auto mb-3 bg-[#001a00] border border-[#003B00] rounded-lg flex items-center justify-center">
                <Music className={`h-10 w-10 text-[#00FF41] ${isPlaying ? 'animate-pulse' : 'opacity-40'}`} />
              </div>
              <p className="text-lg text-[#00FF41] font-bold truncate" data-testid="now-playing-title">
                {currentTrack?.title || 'No track selected'}
              </p>
              <p className="text-xs text-[#008F11]">{currentTrack?.artist || 'BitBot AI'}</p>
            </div>

            {/* Error display */}
            {audioError && (
              <div className="text-red-500 text-xs text-center mb-2 px-4 py-2 bg-red-900/20 rounded" data-testid="audio-error">
                Audio error: {audioError}
              </div>
            )}

            {/* Direct download test link - if this works, the file is valid */}
            {currentTrack && audioError && (
              <div className="mb-3 p-2 border border-yellow-700/50 bg-yellow-900/10 rounded text-center">
                <a
                  href={`${API_V2}/music/tracks/${currentTrack.track_id}/stream`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-yellow-400 text-xs underline"
                  data-testid="download-test-link"
                >
                  Tap here to test direct audio download
                </a>
                <p className="text-yellow-400/60 text-xs mt-1">If this downloads/plays, the file is valid</p>
              </div>
            )}

            {/* Progress */}
            <div className="flex items-center gap-3 mb-4">
              <span className="text-[10px] text-[#008F11] w-10 text-right">{fmt(progress)}</span>
              <Slider
                value={[progress]}
                max={duration || 100}
                step={0.5}
                onValueChange={(v) => { if (audioRef.current) audioRef.current.currentTime = v[0]; }}
                className="flex-1"
                data-testid="music-progress-slider"
              />
              <span className="text-[10px] text-[#008F11] w-10">{fmt(duration)}</span>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-center gap-4 mb-4">
              <Button variant="ghost" size="icon" onClick={() => setShuffle(p => !p)} className={shuffleOn ? 'text-[#00FF41]' : 'text-[#333]'} data-testid="shuffle-btn">
                <Shuffle className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={playPrev} className="text-[#00FF41]" data-testid="prev-btn">
                <SkipBack className="h-5 w-5" />
              </Button>
              <Button
                size="icon"
                data-testid="play-pause-btn"
                className="h-12 w-12 rounded-full bg-[#00FF41] text-black hover:bg-[#00CC33]"
                onClick={() => {
                  if (!currentTrack && tracks.length > 0) {
                    playTrack(tracks[0]);
                  } else if (currentTrack) {
                    playTrack(currentTrack);
                  }
                }}
              >
                {isPlaying ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6 ml-0.5" />}
              </Button>
              <Button variant="ghost" size="icon" onClick={playNext} className="text-[#00FF41]" data-testid="next-btn">
                <SkipForward className="h-5 w-5" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => setMuted(m => { const n = !m; if (audioRef.current) audioRef.current.volume = n ? 0 : vol / 100; return n; })} className="text-[#00FF41]" data-testid="mute-btn">
                {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
              </Button>
            </div>

            {/* Volume */}
            <div className="flex items-center gap-3 max-w-[200px] mx-auto">
              <VolumeX className="h-3 w-3 text-[#333]" />
              <Slider
                value={[muted ? 0 : vol]}
                max={100}
                step={1}
                onValueChange={(v) => { setVol(v[0]); setMuted(false); if (audioRef.current) audioRef.current.volume = v[0] / 100; }}
                className="flex-1"
                data-testid="volume-slider"
              />
              <Volume2 className="h-3 w-3 text-[#008F11]" />
            </div>
          </CardContent>
        </Card>

        {/* Track List */}
        <Card className="bg-[#0a0a0a] border border-[#003B00] mb-6" data-testid="track-list-card">
          <CardHeader className="border-b border-[#003B00] p-3">
            <CardTitle className="text-sm tracking-widest flex items-center gap-2">
              <Music className="h-4 w-4" /> TRACKS ({tracks.length}/10)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="max-h-[400px]">
              {tracks.length === 0 ? (
                <div className="text-center py-8 text-[#008F11] text-sm">
                  No tracks uploaded yet. {isAdmin ? 'Use the upload section below.' : 'Ask an admin to upload tracks.'}
                </div>
              ) : (
                tracks.map((track, idx) => (
                  <div
                    key={track.track_id}
                    data-testid={`track-row-${track.track_id}`}
                    className={`px-3 py-3 border-b border-[#111] hover:bg-[#001a00] cursor-pointer transition-colors ${
                      currentTrack?.track_id === track.track_id ? 'bg-[#001a00] border-l-2 border-l-[#00FF41]' : ''
                    } ${!track.enabled ? 'opacity-40' : ''}`}
                    onClick={() => playTrack(track)}
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-6 text-center text-xs text-[#008F11] shrink-0">
                        {currentTrack?.track_id === track.track_id && isPlaying ? (
                          <div className="flex items-end justify-center gap-[2px] h-4">
                            <div className="w-[3px] bg-[#00FF41] animate-pulse" style={{ height: '60%', animationDelay: '0ms' }} />
                            <div className="w-[3px] bg-[#00FF41] animate-pulse" style={{ height: '100%', animationDelay: '150ms' }} />
                            <div className="w-[3px] bg-[#00FF41] animate-pulse" style={{ height: '40%', animationDelay: '300ms' }} />
                          </div>
                        ) : (
                          <span>{idx + 1}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-[#00FF41] font-bold truncate">{track.title}</p>
                        <p className="text-[10px] text-[#008F11]">{track.artist} &middot; {(track.file_size / (1024 * 1024)).toFixed(1)}MB</p>
                      </div>
                    </div>
                    {isAdmin && (
                      <div className="flex flex-wrap items-center gap-1 mt-2 ml-8" onClick={e => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" className="h-7 px-1.5 text-[#008F11] text-[10px]" onClick={() => {}} disabled={idx === 0}>
                          <ChevronUp className="h-3 w-3" /> Up
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 px-1.5 text-[#008F11] text-[10px]" onClick={() => {}} disabled={idx === tracks.length - 1}>
                          <ChevronDown className="h-3 w-3" /> Dn
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 px-1.5 text-[10px]" onClick={() => handleToggle(track.track_id)}>
                          {track.enabled ? <><ToggleRight className="h-3 w-3 text-[#00FF41]" /> On</> : <><ToggleLeft className="h-3 w-3 text-[#333]" /> Off</>}
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 px-1.5 text-red-600 hover:text-red-400 text-[10px]" onClick={() => handleDelete(track.track_id)} data-testid={`delete-track-${track.track_id}`}>
                          <Trash2 className="h-3 w-3" /> Del
                        </Button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Upload Section (Admin) */}
        {isAdmin && (
          <Card className="bg-[#0a0a0a] border border-[#003B00]" data-testid="upload-section">
            <CardHeader className="border-b border-[#003B00] p-3">
              <CardTitle className="text-sm tracking-widest flex items-center gap-2">
                <Upload className="h-4 w-4" /> UPLOAD TRACK
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-3">
              <Input placeholder="Track title (optional)" value={uploadTitle} onChange={e => setUploadTitle(e.target.value)} className="bg-black border-[#003B00] text-[#00FF41] placeholder:text-[#333]" data-testid="upload-title-input" />
              <div className="flex flex-wrap items-center gap-3">
                <label className={`flex items-center gap-2 px-4 py-2 border border-[#003B00] text-sm cursor-pointer hover:bg-[#001a00] transition-colors ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
                  <Upload className="h-4 w-4" />
                  {uploading ? `Uploading... ${uploadProgress}%` : 'Choose File'}
                  <input type="file" accept=".mp3,.wav,.m4a,.ogg" onChange={handleUpload} className="hidden" disabled={uploading} data-testid="upload-file-input" />
                </label>
                <span className="text-[10px] text-[#333]">mp3, wav, m4a, ogg (max 50MB)</span>
              </div>
              {uploading && (
                <div className="w-full h-2 bg-[#111] rounded-full overflow-hidden mt-2">
                  <div className="h-full bg-[#00FF41] transition-all" style={{ width: `${uploadProgress}%` }} />
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default MusicPage;
